import { useState } from 'react';
import {
  Container,
  Box,
  Flex,
  Select,
  Heading,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useHistoricalRocks } from './useHistoricalRocks';
import { HistoricalRockCard } from './components/HistoricalRockCard';
import { useRocks } from '../Rocks/useRocks';

/**
 * Historical Rocks Page - View achieved and dropped rocks from past quarters
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function HistoricalRocksPage() {
  const { currentUser } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [filterType, setFilterType] = useState<'all' | 'achieved' | 'dropped'>('all');

  const {
    rocks,
    users,
    loading,
    getFilteredRocks,
    getStats,
  } = useHistoricalRocks(currentUser?.uid);

  const { updateRock } = useRocks(currentUser?.uid);

  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const filterLabelColor = useColorModeValue('gray.700', 'gray.300');
  const selectBg = useColorModeValue('white', 'gray.700');
  const statBg = useColorModeValue('gray.50', 'gray.800');

  const filteredRocks = getFilteredRocks(filterType, selectedUserId);
  const stats = getStats(selectedUserId);

  const handleTriage = async (rockId: string) => {
    // Reactivate a dropped rock by resetting its dropped status
    try {
      // Use any to allow isDropped field which exists in Rock type
      await updateRock(rockId, {
        isDropped: false,
        droppedAt: null,
        status: 'on-track',
        progress: 0,
      } as any);
    } catch (error) {
      console.error('Error reactivating rock:', error);
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text color="gray.500">Loading historical rocks...</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <Box>
          <Heading size="lg" color={headingColor} mb={2}>
            Historical Rocks
          </Heading>
          <Text color={subheadingColor}>
            View achieved and dropped rocks from past quarters
          </Text>
        </Box>
      </Flex>

      {/* User Filter */}
      <Box mb={6}>
        <Flex align="center" gap={4}>
          <Text color={filterLabelColor} fontWeight="medium">
            Filter by User:
          </Text>
          <Select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            bg={selectBg}
            maxW="300px"
          >
            <option value="all">All Users</option>
            {users
              .filter((user) => user.isEOSUser)
              .map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
          </Select>
        </Flex>
      </Box>

      {/* Statistics */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Stat bg={statBg} p={4} borderRadius="md">
          <StatLabel>Total Historical</StatLabel>
          <StatNumber>{stats.total}</StatNumber>
        </Stat>
        <Stat bg={statBg} p={4} borderRadius="md">
          <StatLabel>Achieved</StatLabel>
          <StatNumber color="green.500">{stats.achieved}</StatNumber>
        </Stat>
        <Stat bg={statBg} p={4} borderRadius="md">
          <StatLabel>Dropped</StatLabel>
          <StatNumber color="red.500">{stats.dropped}</StatNumber>
        </Stat>
      </SimpleGrid>

      {/* Tabs */}
      <Tabs
        index={filterType === 'all' ? 0 : filterType === 'achieved' ? 1 : 2}
        onChange={(index) => {
          const types: ('all' | 'achieved' | 'dropped')[] = ['all', 'achieved', 'dropped'];
          setFilterType(types[index]);
        }}
        colorScheme="blue"
      >
        <TabList>
          <Tab>All</Tab>
          <Tab>Achieved</Tab>
          <Tab>Dropped</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            {filteredRocks.length === 0 ? (
              <Box textAlign="center" py={12}>
                <Text color={subheadingColor}>No historical rocks found</Text>
              </Box>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mt={4}>
                {filteredRocks.map((rock) => (
                  <HistoricalRockCard
                    key={rock.id}
                    rock={rock}
                    users={users}
                    onTriage={handleTriage}
                  />
                ))}
              </SimpleGrid>
            )}
          </TabPanel>
          <TabPanel px={0}>
            {filteredRocks.length === 0 ? (
              <Box textAlign="center" py={12}>
                <Text color={subheadingColor}>No achieved rocks found</Text>
              </Box>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mt={4}>
                {filteredRocks.map((rock) => (
                  <HistoricalRockCard
                    key={rock.id}
                    rock={rock}
                    users={users}
                    onTriage={handleTriage}
                  />
                ))}
              </SimpleGrid>
            )}
          </TabPanel>
          <TabPanel px={0}>
            {filteredRocks.length === 0 ? (
              <Box textAlign="center" py={12}>
                <Text color={subheadingColor}>No dropped rocks found</Text>
              </Box>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mt={4}>
                {filteredRocks.map((rock) => (
                  <HistoricalRockCard
                    key={rock.id}
                    rock={rock}
                    users={users}
                    onTriage={handleTriage}
                  />
                ))}
              </SimpleGrid>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Container>
  );
}

